import { css } from "@emotion/css";
import {
  Button,
  Modal,
  ModalBody,
  type ModalButtonProps,
  ModalFooter,
  ModalHeader,
  Sheets,
  type SheetsProps,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { zIndex } from "../Variables";

export type ModalSheetProps = Pick<SheetsProps, "size"> & {
  id?: string;
  visible?: boolean;
  onClose: () => void;
  onBack?: () => void;
  title: string;
  children?: ReactNode;
  primaryButtonProps?: ModalButtonProps;
  secondaryButtonProps?: ModalButtonProps;
  footer?: ReactNode;
};

export const ModalSheet = ({
  id,
  visible,
  onClose,
  onBack,
  title,
  children,
  primaryButtonProps,
  secondaryButtonProps,
  size,
  footer,
}: ModalSheetProps) => {
  const [root, setRoot] = useState<any>();

  // This is to keep react testing library happy
  useEffect(() => {
    setRoot(document.querySelector("#modal-portal"));
  }, []);

  if (!root) {
    return null;
  }
  return createPortal(
    <Modal
      id={id}
      data-testid={id}
      className={css`
        z-index: ${zIndex.modalSheet} !important; // Have to have important here to override skapa
      `}
      handleCloseBtn={onClose}
      visible={visible}
      // prevent click events from bubbling up and triggering events behind the modal sheet
      onClick={(e) => e.stopPropagation()}
    >
      <Sheets
        aria-label={title}
        closeBtnClick={onClose}
        header={
          <ModalHeader
            title={title}
            backBtnClick={onBack}
            ariaBackBtnTxt={t("action.go_back")}
          />
        }
        size={size}
        footer={
          footer || primaryButtonProps || secondaryButtonProps ? (
            <ModalFooter closeBtnClick={onClose} renderBorder>
              {footer ?? (
                <>
                  {secondaryButtonProps && (
                    <Button
                      type="secondary"
                      disabled={secondaryButtonProps.disabled}
                      text={secondaryButtonProps?.text}
                      onClick={secondaryButtonProps?.onClick ?? onClose}
                      loading={secondaryButtonProps?.loading}
                    />
                  )}
                  {primaryButtonProps && (
                    <Button
                      type="primary"
                      text={primaryButtonProps?.text}
                      disabled={primaryButtonProps?.disabled}
                      onClick={primaryButtonProps?.onClick}
                      loading={primaryButtonProps?.loading}
                    />
                  )}
                </>
              )}
            </ModalFooter>
          ) : null
        }
      >
        <ModalBody>{children}</ModalBody>
      </Sheets>
    </Modal>,
    root
  );
};
